import {
    Injectable,
    OnDestroy,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import { TranslateService } from '@ngx-translate/core';
import {
    IDropdownOption,
    UiGridIntl,
} from '@uipath/angular/components/ui-grid';

import { Subject } from 'rxjs';
import {
    map,
    takeUntil,
} from 'rxjs/operators';

const TRANSLATE_KEYS = [
    'grid_search_tooltip',
    'grid_search_placeholder',
    'grid_action_refresh',
    'grid_filter_option_all',
    'no_data_general',
    'grid_search_tooltip_clear',
    'clear_selection',
    'multipage_selection_info',
    'a11y_loading_page',
    'a11y_grid_refreshing',
    'a11y_grid_showing_page_no',
    'a11y_sort_direction_asc',
    'a11y_sort_direction_desc',
];

@Injectable()
export class UiTableIntl extends UiGridIntl implements OnDestroy {
    private _destroyed$ = new Subject();

    constructor(
        private _translate: TranslateService,
        appState: AppStateFacade,
    ) {
        super();

        appState.language.value$
            .pipe(
                map(() =>
                    TRANSLATE_KEYS.reduce(
                        (translations, key) => {
                            translations[key] = _translate.instant(key);
                            return translations;
                        }, {} as Record<string, string>),
                ),
                takeUntil(this._destroyed$),
            ).subscribe(this._updateLabels);
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public translateDropdownOption = (option: IDropdownOption) =>
        !!option.translationKey ?
            this._translate.instant(option.translationKey) :
            option.label

    public translateMultiPageSelectionCount =
        (count: number) => {
            if (count !== 1) {
                return `${count} ${this._translate.instant('grid_label_selected_zero_or_more')}`;
            }

            const [first, second] = this._translate.instant('grid_label_selected_one_row').split('|');

            return `${first || ''}${second || ''}`;
        }

    public loadingPage = (page: number) =>
        this._translate.instant('a11y_loading_page', { page })

    public loadedPage = (page: number, showingItems: number, total: number) =>
        this._translate.instant('a11y_grid_showing_page_no', { page, showingItems, total })

    public columnSortedAscending = (columnTitle: string) =>
        this._translate.instant('a11y_grid_sort_status', {
            columnTitle,
            direction: this.ascending,
        })

    public columnSortedDescending = (columnTitle: string) =>
        this._translate.instant('a11y_grid_sort_status', {
            columnTitle,
            direction: this.descending,
        })

    public columnUnsorted = (columnTitle: string) =>
        this._translate.instant('a11y_grid_sort_status_unsorted', {
            columnTitle,
        })


    private _updateLabels = ({
        grid_search_tooltip,
        grid_search_placeholder,
        grid_action_refresh,
        grid_filter_option_all,
        no_data_general,
        grid_search_tooltip_clear,
        clear_selection,
        multipage_selection_info,
        a11y_grid_refreshing,
        a11y_sort_direction_asc,
        a11y_sort_direction_desc,
    }: Record<string, string>) => {
        this.ascending = a11y_sort_direction_asc;
        this.descending = a11y_sort_direction_desc;
        this.searchTooltip = grid_search_tooltip;
        this.refreshTooltip = grid_action_refresh;
        this.clearSelectionTooltip = clear_selection;

        this.noSelectionMessage = `0 ${this._translate.instant('grid_label_selected_zero_or_more')}`;
        this.multiPageSelectionInfoTooltip = multipage_selection_info;

        this.noFilterPlaceholder = grid_filter_option_all;
        this.searchPlaceholder = grid_search_placeholder;
        this.noDataMessage = no_data_general;
        this.clearTooltip = grid_search_tooltip_clear;

        this.pageRefreshing = a11y_grid_refreshing;

        this.changes.next();
    }
}
